// src/components/dashboard/DesktopSidebar.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Flex } from '@chakra-ui/react'
import { LuChevronDown } from 'react-icons/lu'

type DesktopSidebarProps = {
  collapsed: boolean
  widthPx: number
  headerHeightPx?: number
  pagePaddingPx?: number
  children: React.ReactNode

  /** divider options */
  showDivider?: boolean
  dividerZIndex?: number
}

export function DesktopSidebar({
  collapsed,
  widthPx,
  headerHeightPx = 72,
  pagePaddingPx = 16,
  children,
  showDivider = true,
  dividerZIndex = 1,
}: DesktopSidebarProps): React.ReactElement {
  const sidebarW = collapsed ? 0 : widthPx

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [showScrollHint, setShowScrollHint] = useState(false)

  const updateHint = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const hasOverflow = el.scrollHeight > el.clientHeight + 1
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2
    setShowScrollHint(hasOverflow && !atBottom)
  }, [])

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    requestAnimationFrame(updateHint)
  }, [collapsed, updateHint])

  const stickyTopPx = useMemo(
    () => headerHeightPx + pagePaddingPx,
    [headerHeightPx, pagePaddingPx]
  )
  const heightCss = useMemo(
    () => `calc(100vh - ${headerHeightPx}px - ${pagePaddingPx * 2}px)`,
    [headerHeightPx, pagePaddingPx]
  )

  return (
    <>
      {/* Divider line (fixed, not inside sticky/scroll container) */}
      {showDivider && (
        <Box
          display={{ base: 'none', md: 'block' }}
          position="fixed"
          top={`${headerHeightPx}px`}
          bottom="0"
          left={`${sidebarW}px`}
          width="2px"
          bg="bg.muted"
          pointerEvents="none"
          zIndex={dividerZIndex}
          opacity={collapsed ? 0 : 1}
          transition="opacity 0.2s ease"
        />
      )}

      {/* Sidebar */}
      <Box
        display={{ base: 'none', md: 'block' }}
        position="sticky"
        top={`${stickyTopPx}px`}
        height={heightCss}
        flexShrink={0}
        width={`${sidebarW}px`}
        minW={`${sidebarW}px`}
        maxW={`${sidebarW}px`}
        opacity={collapsed ? 0 : 1}
        pointerEvents={collapsed ? 'none' : 'auto'}
        overflow="visible"
        transition="width 250ms ease, min-width 250ms ease, max-width 250ms ease, opacity 150ms ease"
        willChange="width, opacity"
      >
        <Flex h="full" direction="column" overflow="hidden" position="relative">
          <Flex
            ref={node => {
              scrollRef.current = node as unknown as HTMLDivElement | null
            }}
            flex={1}
            direction="column"
            overflow={collapsed ? 'hidden' : 'auto'}
            px={4}
            onScroll={updateHint}
            onMouseEnter={updateHint}
            onTouchStart={updateHint}
            css={{
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: 'transparent' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&:hover::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.25)' },
              scrollbarWidth: 'none',
            }}
          >
            {children}
          </Flex>

          <Flex
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            align="center"
            justify="center"
            h="44px"
            cursor="pointer"
            color="fg.muted"
            bg="bg"
            transition="opacity 0.2s ease"
            opacity={showScrollHint && !collapsed ? 1 : 0}
            pointerEvents={showScrollHint && !collapsed ? 'auto' : 'none'}
            onClick={scrollToBottom}
          >
            <LuChevronDown size={24} />
          </Flex>
        </Flex>
      </Box>
    </>
  )
}
